import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import { useUser } from "../context/UserContext";
import { uploadWorkorderAttachment } from "../services/workorderDocumentService";
import { exportWorkorderPdf } from "../utils/workorderPdfExport";
import {
  getApiErrorMessage,
  getApiFieldErrors,
  hasApiErrorCode,
  showApiErrorToast,
} from "../utils/apiError";
import {
  formatPlannedMinutes,
  parsePositiveWholeMinutes,
  resolvePlannedMinutes,
} from "../utils/plannedMinutes";
import { getWorkorderPhotos } from "../services/workorderActivityService";
import { API_BASE_URL } from "../config/api";

/* Cards */
import OpdrachtInfoCard from "../components/workbon/OpdrachtInfoCard";
import WerkOpdrachtCard from "../components/workbon/WerkOpdrachtCard";
import UrenCard from "../components/workbon/UrenCard";
import FotosCard from "../components/workbon/FotosCard";
import MaterialenCard from "../components/workbon/MaterialenCard";
import AfrondenCard from "../components/workbon/AfrondenCard";
import ProjectLeaderDaylogTabs from "../components/workorder/ProjectLeaderDaylogTabs";


/* Modal */
import FotoUploadMetPintura from "../components/modals/FotoUploadMetPintura";

function getWerkbonClientName(werkbon) {
  return (
    werkbon?.clientId?.naam ||
    werkbon?.client?.naam ||
    werkbon?.clientName ||
    werkbon?.klant?.naam ||
    "-"
  );
}

function getWerkbonProjectName(werkbon) {
  return (
    werkbon?.projectId?.naam ||
    werkbon?.projectId?.title ||
    werkbon?.project?.naam ||
    werkbon?.project?.title ||
    werkbon?.projectName ||
    werkbon?.projectNummer ||
    "-"
  );
}

function getWerkbonContactFields(werkbon) {
  return {
    contactpersoon: werkbon?.contact?.name || "-",
    functie: werkbon?.contact?.role || "-",
    telefoon: werkbon?.contact?.phone || "-",
    email: werkbon?.contact?.email || "-",
  };
}

export default function WerkbonDetail() {

  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();

  const [werkbon, setWerkbon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [financial, setFinancial] = useState(null);
  const [financialError, setFinancialError] = useState("");
  const [factuurMakenLoading, setFactuurMakenLoading] = useState(false);
  const [timeDecisionLoading, setTimeDecisionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [bijlageUploading, setBijlageUploading] = useState(false);
  const [bijlageDeletingId, setBijlageDeletingId] = useState("");
  const [bijlageError, setBijlageError] = useState("");
  const [uploadNotice, setUploadNotice] = useState("");
  const [planningPermissions, setPlanningPermissions] = useState([]);
  const [planningUsers, setPlanningUsers] = useState([]);
  const [planningForm, setPlanningForm] = useState({
    plannedDate: "",
    plannedStartTime: "",
    assignedTo: "",
    plannedMinutes: "120",
  });
  const [planningSaving, setPlanningSaving] = useState(false);
  const [planningError, setPlanningError] = useState("");
  const [planningFieldErrors, setPlanningFieldErrors] = useState({});
  const [workflowSaving, setWorkflowSaving] = useState(false);
  const [projectLinkSaving, setProjectLinkSaving] = useState(false);
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectOptionsLoading, setProjectOptionsLoading] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [werkopdrachtText, setWerkopdrachtText] = useState("");
  const [werkopdrachtSaving, setWerkopdrachtSaving] = useState(false);
  const [werkopdrachtError, setWerkopdrachtError] = useState("");
  const [afrondStatusNotice, setAfrondStatusNotice] = useState("");
  const [tuImportMessage, setTuImportMessage] = useState("");
  const [tuImportError, setTuImportError] = useState("");
  const [mailTo, setMailTo] = useState("");
  const [mailSubject, setMailSubject] = useState("");
  const [mailBody, setMailBody] = useState("");
  const [mailSending, setMailSending] = useState(false);
  const [mailError, setMailError] = useState("");
  const [mailNotice, setMailNotice] = useState("");
  const [pdfExporting, setPdfExporting] = useState(false);
  const [latestPhotoObjectUrls, setLatestPhotoObjectUrls] = useState({});
  const [latestWorkorderPhotos, setLatestWorkorderPhotos] = useState([]);

  const [fotoModalOpen, setFotoModalOpen] = useState(false);
  const canEditPlanning = useMemo(
    () =>
      planningPermissions.includes("workorder.edit") ||
      planningPermissions.includes("workorders.plan") ||
      planningPermissions.includes("workorders.edit_preparation") ||
      planningPermissions.includes("tenant.admin"),
    [planningPermissions]
  );
  const roleNames = useMemo(() => getRoleNames(user), [user]);
  const canReassignProject = useMemo(
    () =>
      planningPermissions.includes("workorder.edit") ||
      planningPermissions.includes("workorders.plan") ||
      planningPermissions.includes("workorders.edit_preparation") ||
      planningPermissions.includes("tenant.admin") ||
      roleNames.some((role) =>
        ["projectleider", "tenantadmin", "platformadmin", "werkvoorbereider", "admin"].includes(role)
      ),
    [planningPermissions, roleNames]
  );
  const canEditWerkopdracht = canReassignProject;
  const canSendWorkorderMail = canReassignProject;
  const planningRequirements = useMemo(
    () => getPlanningRequirements(werkbon, planningForm),
    [werkbon, planningForm]
  );
  const bronOpnameDocumentLinks = useMemo(
    () =>
      Array.isArray(werkbon?.bronDocumentLinks)
        ? werkbon.bronDocumentLinks.filter(
            (item) => String(item?.sourceType || "").trim().toLowerCase() === "opname"
          )
        : [],
    [werkbon?.bronDocumentLinks]
  );
  const latestPhotoPreviewItems = useMemo(
    () => getLatestPhotoPreviewItems(werkbon, latestWorkorderPhotos),
    [werkbon, latestWorkorderPhotos]
  );
  const printContactFields = useMemo(() => getWerkbonContactFields(werkbon), [werkbon]);

  useEffect(() => {
    const securePhotos = latestPhotoPreviewItems.filter((photo) =>
      isSecureFileUrl(photo?.thumbUrl || photo?.url || "")
    );
    if (securePhotos.length === 0) {
      setLatestPhotoObjectUrls({});
      return undefined;
    }

    let cancelled = false;
    const objectUrls = [];

    (async () => {
      const entries = await Promise.all(
        securePhotos.map(async (photo) => {
          try {
            const blob = await fetchDocumentBlob(photo.thumbUrl || photo.url);
            const objectUrl = window.URL.createObjectURL(blob);
            objectUrls.push(objectUrl);
            return [photo.id, objectUrl];
          } catch {
            return [photo.id, ""];
          }
        })
      );

      if (cancelled) {
        objectUrls.forEach((url) => window.URL.revokeObjectURL(url));
        return;
      }

      setLatestPhotoObjectUrls(
        Object.fromEntries(entries.filter(([key, value]) => key && value))
      );
    })();

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => window.URL.revokeObjectURL(url));
    };
  }, [latestPhotoPreviewItems]);


  // ===============================
  // Werkbon ophalen
  // ===============================
  const loadWerkbon = useCallback(async () => {

    try {
      setLoading(true);

      const [res, fin, photos] = await Promise.all([
        api.get(`/werkbonnen/${id}`),
        api.get(`/werkbonnen/${id}/financial`),
        getWorkorderPhotos(id).catch(() => []),
      ]);

      setWerkbon(res.data);
      setLatestWorkorderPhotos(Array.isArray(photos) ? photos.filter(Boolean) : []);
      setWerkopdrachtText(getWerkopdrachtLabel(res.data));
      setPlanningForm({
        plannedDate: toDateInputValue(res.data?.plannedDate),
        plannedStartTime: String(res.data?.plannedStartTime || ""),
        assignedTo: getAssignedToValue(res.data),
        plannedMinutes: String(resolvePlannedMinutes(res.data)),
      });
      setFinancial(fin.data || null);
      setFinancialError("");

    } catch (err) {

      console.error("Fout bij laden werkbon:", err);
      setError(getApiErrorMessage(err, "Werkbon kon niet worden geladen."));
      setFinancialError(getApiErrorMessage(err, "Financieel overzicht kon niet worden geladen."));

    } finally {
      setLoading(false);
    }
  }, [id]);

  const saveWerkopdracht = async () => {
    if (!canEditWerkopdracht || !werkbon?._id) return;
    const nextValue = String(werkopdrachtText || "").trim();
    if (!nextValue) {
      setWerkopdrachtError("Werkopdracht mag niet leeg zijn.");
      return;
    }

    try {
      setWerkopdrachtSaving(true);
      setWerkopdrachtError("");
      await api.patch(`/werkbonnen/${id}`, {
        opdracht: { omschrijving: nextValue },
        scopeSamenvatting: nextValue,
        omschrijving: nextValue,
      });
      await loadWerkbon();
    } catch (err) {
      const nextFieldErrors = getApiFieldErrors(err);
      if (hasApiErrorCode(err, "VALIDATION_ERROR")) {
        setWerkopdrachtError(
          firstFieldError(nextFieldErrors, ["omschrijving", "opdracht.omschrijving", "scopeSamenvatting"]) ||
            "Controleer de werkopdracht en probeer opnieuw."
        );
      } else {
        setWerkopdrachtError(getApiErrorMessage(err, "Werkopdracht opslaan mislukt"));
      }
    } finally {
      setWerkopdrachtSaving(false);
    }
  };

  const sendWorkorderMail = async () => {
    const recipient = String(mailTo || "").trim();
    const subject = String(mailSubject || "").trim();
    const body = String(mailBody || "").trim();

    if (!recipient) {
      setMailError("Vul een ontvanger in.");
      return;
    }
    if (!subject) {
      setMailError("Vul een onderwerp in.");
      return;
    }
    if (!body) {
      setMailError("Vul een bericht in.");
      return;
    }

    try {
      setMailSending(true);
      setMailError("");
      setMailNotice("");
      await api.post("/email/messages/send", {
        workorderId: id,
        recipients: [recipient],
        subject,
        body,
      });
      setMailNotice("Mail verzonden.");
      setMailBody("");
    } catch (err) {
      setMailError(getApiErrorMessage(err, "Mail verzenden mislukt"));
    } finally {
      setMailSending(false);
    }
  };


  useEffect(() => {
    loadWerkbon();
  }, [loadWerkbon]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const importStatus = String(
      params.get("ociImport") || params.get("tuImport") || ""
    )
      .trim()
      .toLowerCase();
    if (!importStatus) return;

    const importedCount = Number(
      params.get("ociImportedCount") || params.get("tuImportedCount") || 0
    );
    const errorMessage = String(
      params.get("ociError") || params.get("tuError") || ""
    ).trim();

    if (importStatus === "success") {
      setTuImportError("");
      setTuImportMessage(
        importedCount > 0
          ? `${importedCount} producten succesvol geimporteerd.`
          : "Producten succesvol geimporteerd."
      );
    } else {
      setTuImportMessage("");
      setTuImportError(errorMessage || "Bestelling niet afgerond, probeer opnieuw");
    }

    params.delete("ociImport");
    params.delete("ociImportedCount");
    params.delete("ociError");
    params.delete("tuImport");
    params.delete("tuImportedCount");
    params.delete("tuError");

    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    const warning = String(location.state?.uploadWarning || "").trim();
    if (warning) {
      setUploadNotice(warning);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    async function loadProjects() {
      try {
        setProjectOptionsLoading(true);
        const res = await api.get("/projects");
        setProjectOptions(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        setProjectOptions([]);
      } finally {
        setProjectOptionsLoading(false);
      }
    }

    loadProjects();
  }, []);

  useEffect(() => {
    const projectIdValue = getProjectIdValue(werkbon);
    if (projectIdValue) {
      setSelectedProjectId(projectIdValue);
    }
  }, [werkbon]);

  useEffect(() => {
    const defaultEmail = String(werkbon?.contact?.email || "").trim();
    const defaultSubject = `Werkbon ${String(werkbon?.werkbonNummer || werkbon?._id || "").trim()}`.trim();

    if (defaultEmail) {
      setMailTo((current) => String(current || "").trim() || defaultEmail);
    }
    setMailSubject((current) => {
      const trimmed = String(current || "").trim();
      if (trimmed) return current;
      return defaultSubject;
    });
  }, [werkbon]);

  useEffect(() => {
    async function loadPlanningAccess() {
      try {
        const me = await api.get("/rbac/me");
        const permissions = Array.isArray(me.data?.permissions)
          ? me.data.permissions.map((item) => String(item || "").trim().toLowerCase())
          : [];
        setPlanningPermissions(permissions);

        if (
          permissions.includes("workorder.edit") ||
          permissions.includes("workorders.plan") ||
          permissions.includes("workorders.edit_preparation") ||
          permissions.includes("tenant.admin")
        ) {
          const usersRes = await api.get("/users?role=Monteur");
          setPlanningUsers(asUsersArray(usersRes.data));
        }
      } catch (err) {
        setPlanningPermissions([]);
        setPlanningUsers([]);
      }
    }

    loadPlanningAccess();
  }, []);


  // ===============================
  // Acties
  // ===============================
  const afrondenWerkbon = async () => {
    setAfrondStatusNotice("");
    async function attempt(payload) {
      try {
        await api.post(`/werkbonnen/${id}/afmelden`, payload);
        setAfrondStatusNotice("");
        await loadWerkbon();
      } catch (err) {
        const message = getApiErrorMessage(err, "Afronden mislukt");

        if (message.includes("confirmZeroHours=true")) {
          const confirmed = window.confirm(
            "Er zijn nog geen uren geregistreerd. Wil je deze werkbon afronden met expliciet 0 uur?"
          );
          if (confirmed) {
            return attempt({ ...payload, confirmZeroHours: true });
          }
        }

        if (message.includes("confirmNoMaterial=true")) {
          const confirmed = window.confirm(
            "Er is geen materiaal geregistreerd. Wil je afronden en expliciet bevestigen dat er geen materiaal is gebruikt?"
          );
          if (confirmed) {
            return attempt({ ...payload, confirmNoMaterial: true });
          }
        }

        if (message.includes("confirmNoEvidence=true")) {
          const confirmed = window.confirm(
            "Er zijn geen foto's of bijlagen toegevoegd. Wil je afronden en expliciet bevestigen dat bewijs niet nodig is?"
          );
          if (confirmed) {
            return attempt({ ...payload, confirmNoEvidence: true });
          }
        }

        if (message.includes("confirmNoExecutionLog=true")) {
          const confirmed = window.confirm(
            "Er is geen daglog/uitvoeringstekst ingevuld. Wil je afronden en expliciet bevestigen dat daglog ontbreekt?"
          );
          if (confirmed) {
            return attempt({ ...payload, confirmNoExecutionLog: true });
          }
        }

        setAfrondStatusNotice(message);
      }
    }

    await attempt({
      afmeldcode: "Werk afgerond",
    });
  };

  const maakFactuurSnapshot = async () => {
    try {
      setFactuurMakenLoading(true);
      const res = await api.post(`/werkbonnen/${id}/facturen`, {
        status: "draft",
      });
      alert(
        `Factuur-snapshot gemaakt${res?.data?.factuur?.invoiceNumber ? `: ${res.data.factuur.invoiceNumber}` : ""}`
      );
      await loadWerkbon();
    } catch (err) {
      showApiErrorToast(err, { fallbackMessage: "Factuur-snapshot maken mislukt" });
    } finally {
      setFactuurMakenLoading(false);
    }
  };

  const decideTimeApproval = async (decision) => {
    if (!werkbon || werkbon.timeMode !== "auto") return;
    if (decision === "afgekeurd" && !String(rejectReason || "").trim()) {
      alert("Vul eerst een afkeurreden in.");
      return;
    }

    try {
      setTimeDecisionLoading(true);
      await api.post(`/werkbonnen/${id}/time-approval/decision`, {
        decision,
        reason: decision === "afgekeurd" ? rejectReason.trim() : undefined,
      });
      if (decision === "goedgekeurd") {
        setRejectReason("");
      }
      await loadWerkbon();
    } catch (err) {
      showApiErrorToast(err, { fallbackMessage: "Beslissing opslaan mislukt" });
    } finally {
      setTimeDecisionLoading(false);
    }
  };

  const handleBijlageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setBijlageError("");
      setBijlageUploading(true);
      await uploadWorkorderAttachment(id, file);
      await loadWerkbon();
    } catch (err) {
      setBijlageError(getApiErrorMessage(err, "Bijlage upload mislukt"));
    } finally {
      setBijlageUploading(false);
      event.target.value = "";
    }
  };

  const handleBijlageDelete = async (bijlage) => {
    const bijlageId = String(bijlage?._id || "").trim();
    if (!bijlageId) {
      setBijlageError("Deze bijlage kan niet worden verwijderd (ongeldig id).");
      return;
    }

    const ok = window.confirm(`Bijlage verwijderen: ${bijlage?.naam || "onbekend bestand"}?`);
    if (!ok) return;

    try {
      setBijlageError("");
      setBijlageDeletingId(bijlageId);
      await api.delete(`/werkbonnen/${id}/bijlage/${bijlageId}`);
      await loadWerkbon();
    } catch (err) {
      setBijlageError(getApiErrorMessage(err, "Bijlage verwijderen mislukt"));
    } finally {
      setBijlageDeletingId("");
    }
  };

  const savePlanning = async () => {
    if (!canEditPlanning) return;

    const plannedMinutes = parsePositiveWholeMinutes(planningForm.plannedMinutes);
    if (!plannedMinutes) {
      setPlanningError("Geplande tijd moet een positief geheel aantal minuten zijn.");
      return;
    }

    try {
      setPlanningSaving(true);
      setPlanningError("");
      setPlanningFieldErrors({});
      await api.put(`/werkbonnen/${id}/planning`, {
        plannedDate: planningForm.plannedDate || null,
        plannedStartTime: planningForm.plannedStartTime || "",
        assignedTo: planningForm.assignedTo || null,
        plannedMinutes,
        plannedDurationMinutes: plannedMinutes,
      });
      await loadWerkbon();
    } catch (err) {
      const nextFieldErrors = getApiFieldErrors(err);
      setPlanningFieldErrors(nextFieldErrors);
      if (hasApiErrorCode(err, "VALIDATION_ERROR")) {
        setPlanningError(
          firstFieldError(nextFieldErrors, [
            "plannedDate",
            "plannedStartTime",
            "assignedTo",
            "plannedMinutes",
            "plannedDurationMinutes",
          ]) || "Controleer de planning en probeer opnieuw."
        );
      } else {
        setPlanningError(getApiErrorMessage(err, "Planning opslaan mislukt"));
      }
    } finally {
      setPlanningSaving(false);
    }
  };

  const sendToWorkvoorraad = async () => {
    if (!canEditPlanning) return;

    try {
      setWorkflowSaving(true);
      setPlanningError("");
      setPlanningFieldErrors({});
      await api.post(`/werkbonnen/${id}/status`, {
        toStatus: "gereed_voor_planning",
      });
      await loadWerkbon();
    } catch (err) {
      setPlanningError(getApiErrorMessage(err, "Werkbon naar werkvoorraad zetten mislukt"));
    } finally {
      setWorkflowSaving(false);
    }
  };

  const directPlanWerkbon = async () => {
    if (!canEditPlanning) return;
    if (!planningRequirements.readyForPlanned) {
      setPlanningError(`Direct inplannen kan nog niet. Verplicht: ${planningRequirements.missingLabels.join(", ")}.`);
      return;
    }

    await savePlanning();
  };

  const changeProjectLink = async (targetId = selectedProjectId) => {
    if (!canReassignProject || !werkbon?._id) return;
    const nextProjectId = String(targetId || "").trim();
    if (!nextProjectId) {
      setPlanningError("Selecteer eerst een project.");
      return;
    }

    try {
      setProjectLinkSaving(true);
      setPlanningError("");
      setPlanningFieldErrors({});
      const currentProjectId = getProjectIdValue(werkbon);
      const targetProject = projectOptions.find((project) => String(project?._id) === nextProjectId);
      const targetProjectLabel = formatProjectLabel(targetProject);
      if (currentProjectId && currentProjectId === nextProjectId) {
        setPlanningError("Werkbon staat al op dit project.");
        return;
      }

      const confirmed = window.confirm(
        `Werkbon ${werkbon?.werkbonNummer || werkbon?._id} verplaatsen naar project "${targetProjectLabel}"?`
      );
      if (!confirmed) return;

      await api.put(`/werkbonnen/${id}/project-link`, {
        targetProjectId: nextProjectId,
        reason: "Handmatige correctie projectkoppeling via werkbon detail",
      });

      await loadWerkbon();
    } catch (err) {
      const nextFieldErrors = getApiFieldErrors(err);
      setPlanningFieldErrors(nextFieldErrors);
      if (hasApiErrorCode(err, "VALIDATION_ERROR")) {
        setPlanningError(
          firstFieldError(nextFieldErrors, ["targetProjectId", "reason"]) ||
            "Controleer de projectwijziging en probeer opnieuw."
        );
      } else {
        setPlanningError(getApiErrorMessage(err, "Project wijzigen mislukt"));
      }
    } finally {
      setProjectLinkSaving(false);
    }
  };

  const downloadWerkbonPdf = async () => {
    if (!werkbon?._id) return;
    try {
      setPdfExporting(true);
      const assignedUserLabel = resolveAssignedUserName(werkbon, planningUsers);
      await exportWorkorderPdf({
        workorder: werkbon,
        financial,
        assignedUserLabel,
        actions: getWerkbonActies(werkbon),
        latestPhotos: getLatestPhotoPreviewItems(werkbon),
        companyDetails: werkbon?.companyDetails || null,
        workorderPdfSnapshot: werkbon?.workorderPdfSnapshot || null,
      });
    } catch (err) {
      window.alert(getApiErrorMessage(err, "PDF export mislukt"));
    } finally {
      setPdfExporting(false);
    }
  };

  const openKamQuickCreate = () => {
    const projectId = String(werkbon?.projectId?._id || werkbon?.projectId || "").trim();
    const clientId = String(werkbon?.clientId?._id || werkbon?.clientId || "").trim();
    const installationId = String(
      werkbon?.logbook?.installationId?._id || werkbon?.logbook?.installationId || ""
    ).trim();
    const query = new URLSearchParams();
    query.set("quickCreate", "1");
    query.set("workorderId", String(id || ""));
    if (projectId) query.set("projectId", projectId);
    if (clientId) query.set("clientId", clientId);
    if (installationId) query.set("installationId", installationId);
    navigate(`/pl/kam/meldingen?${query.toString()}`);
  };


  // ===============================
  // UI
  // ===============================
  if (loading) return <p>Werkbon laden...</p>;

  if (error) {
    return <p className="text-red-600">{error}</p>;
  }

  const printContact = getPrintContactLabel(werkbon);
  const printLocation = getWerkbonLocationLabel(werkbon);
  const printAssignment = resolveAssignedUserName(werkbon, planningUsers);
  const completionSignal = getCompletionSignal(werkbon);
  const completionMissingItems = getCompletionMissingItems(werkbon);
  const hasDaglog =
    (Array.isArray(werkbon?.dailyLogs) && werkbon.dailyLogs.length > 0) ||
    (Array.isArray(werkbon?.daglog) && werkbon.daglog.length > 0);
  const hasPhotos =
    (Array.isArray(werkbon?.photos) && werkbon.photos.length > 0) ||
    (Array.isArray(werkbon?.fotos) && werkbon.fotos.length > 0);
  const hasMaterials =
    (Array.isArray(financial?.materialEntries) && financial.materialEntries.length > 0) ||
    (Array.isArray(werkbon?.materialen) && werkbon.materialen.length > 0);
  const logbookInstallationId = String(
    werkbon?.logbook?.installationId?._id || werkbon?.logbook?.installationId || ""
  ).trim();
  const logbookEntryId = String(werkbon?.logbook?.entryId || "").trim();
  const logbookSyncStatus = String(werkbon?.logbook?.syncStatus || "skipped").trim();

  return (

    <>
      <style>{`
        @media print {
          body {
            background: white !important;
          }

          .no-print {
            display: none !important;
          }

          .print-only {
            display: block !important;
          }

          .print-shell {
            display: block !important;
            height: auto !important;
          }

          .print-shell > div {
            width: 100% !important;
            border: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
          }
        }
      `}</style>

      {(tuImportMessage || tuImportError) && (
        <div className="mx-auto mb-4 max-w-6xl px-4 pt-4">
          {tuImportMessage ? (
            <div className="rounded border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              {tuImportMessage}
            </div>
          ) : null}
          {tuImportError ? (
            <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {tuImportError}
            </div>
          ) : null}
        </div>
      )}

      <div className="print-only hidden p-8 text-black">
        <div className="mx-auto max-w-4xl">
          <div className="border-b pb-4">
            <h1 className="text-2xl font-bold">Werkbon</h1>
            <p className="mt-1 text-sm text-gray-700">
              {werkbon?.werkbonNummer || werkbon?.nummer || "-"}
            </p>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-6 text-sm">
            <PrintField label="Klant" value={getWerkbonClientName(werkbon)} />
            <PrintField label="Project" value={getWerkbonProjectName(werkbon)} />
            <PrintField label="Locatie" value={printLocation} />
            <PrintField label="Status" value={werkbon?.status || "-"} />
            <PrintField label="Datum" value={werkbon?.plannedDate ? formatDatum(werkbon.plannedDate) : "-"} />
            <PrintField label="Monteur" value={printAssignment} />
            <PrintField label="Contactpersoon" value={printContactFields.contactpersoon} />
            <PrintField label="Functie" value={printContactFields.functie} />
            <PrintField label="Telefoon" value={printContactFields.telefoon} />
            <PrintField label="E-mail" value={printContactFields.email} />
            <PrintField label="Starttijd" value={werkbon?.plannedStartTime || "-"} />
          </div>

          <div className="mt-8">
            <h2 className="mb-2 text-lg font-semibold">Werkopdracht</h2>
            <div className="min-h-[140px] rounded border p-4 text-sm whitespace-pre-line">
              {getWerkopdrachtLabel(werkbon)}
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-6">
            <PrintBox title="Notities monteur" />
            <PrintBox title="Gebruikte materialen" />
            <PrintBox title="Handtekening klant" />
            <PrintBox title="Handtekening monteur" />
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl space-y-4 p-4">
        <div className="no-print flex items-start justify-between gap-3 rounded-xl border bg-white p-4 shadow-sm">
          <div>
            <h1 className="text-2xl font-bold">Werkbon</h1>
            <p className="text-sm text-gray-600">
              {werkbon?.werkbonNummer || werkbon?.nummer || "-"} • {werkbon?.status || "-"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openKamQuickCreate}
              className="rounded bg-red-700 px-4 py-2 text-sm text-white hover:bg-red-800"
            >
              Veiligheidsmelding maken
            </button>
            <button
              type="button"
              onClick={downloadWerkbonPdf}
              disabled={pdfExporting}
              className="rounded bg-blue-700 px-4 py-2 text-sm text-white hover:bg-blue-800 disabled:opacity-60"
            >
              {pdfExporting ? "PDF maken..." : "Download PDF"}
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              className="rounded bg-slate-800 px-4 py-2 text-sm text-white hover:bg-slate-900"
            >
              Print werkbon
            </button>
          </div>
        </div>

        {uploadNotice && (
          <div className="no-print rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 shadow-sm">
            {uploadNotice}
          </div>
        )}

        <div className="no-print grid gap-3 md:grid-cols-4">
          <SummaryTile label="Startdatum" value={planningForm.plannedDate ? formatDatum(planningForm.plannedDate) : "-"} />
          <SummaryTile label="Geplande tijd" value={formatPlannedMinutes(planningForm.plannedMinutes)} />
          <SummaryTile label="Monteur" value={printAssignment} />
          <SummaryTile label="Locatie" value={printLocation} />
          <SummaryTile label="Contact" value={printContact} />
        </div>

        <div className="no-print rounded-xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm">
          <p className="text-sm font-semibold text-emerald-900">Laatste resultaatfoto's</p>
          {latestPhotoPreviewItems.length === 0 ? (
            <p className="mt-2 text-sm text-emerald-800">Nog geen foto's op deze werkbon.</p>
          ) : (
            <div className="mt-3 grid gap-2 md:grid-cols-3">
              {latestPhotoPreviewItems.map((photo, index) => (
                <button
                  key={photo.id || `photo-preview-${index}`}
                  type="button"
                  onClick={() => openDocumentInNewTab(photo.url)}
                  className="overflow-hidden rounded-lg border border-emerald-200 bg-white"
                >
                  {latestPhotoObjectUrls[photo.id] ||
                  !isSecureFileUrl(photo.thumbUrl || photo.url) ? (
                    <img
                      src={latestPhotoObjectUrls[photo.id] || photo.thumbUrl || photo.url}
                      alt={`Resultaatfoto ${index + 1}`}
                      onError={(event) => {
                        const target = event.currentTarget;
                        if (photo.url && target.src !== photo.url) {
                          target.src = photo.url;
                        }
                      }}
                      className="h-36 w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-36 w-full items-center justify-center bg-slate-100 text-xs text-slate-500">
                      Laden...
                    </div>
                  )}
                  <p className="px-2 py-1 text-[11px] text-gray-600">
                    {photo.category} • {formatDatumTijd(photo.createdAt)}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="no-print rounded-xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
          <p className="text-sm font-semibold text-slate-900">Rol van deze pagina</p>
          <p className="mt-1 text-sm text-slate-700">
            Deze werkbonpagina is bedoeld voor correctie en aanvulling. Planning/herplanning en
            conflictsturing lopen primair via de planner/dispatch pagina.
          </p>
        </div>

        <div className="no-print rounded-xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-emerald-900">Installatie logboekkoppeling</p>
              <p className="mt-1 text-sm text-emerald-800">
                Status: <span className="font-medium">{logbookSyncStatus || "-"}</span>
                {logbookEntryId ? ` • Entry: ${logbookEntryId}` : ""}
              </p>
            </div>
            {logbookInstallationId ? (
              <a
                href={`/installaties/${logbookInstallationId}?tab=logboek`}
                className="rounded-lg bg-emerald-700 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-800"
              >
                Open logboek
              </a>
            ) : (
              <span className="text-sm text-emerald-800">Nog geen installatie gekoppeld op deze werkbon.</span>
            )}
          </div>
        </div>

        <div className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
          <div className="print-shell space-y-3">
            <SectionTitle title="Kerninfo" subtitle="Klant, project, locatie en opdracht" />
            <OpdrachtInfoCard
              werkbon={werkbon}
              canReassignProject={canReassignProject}
              onReassignProject={() => changeProjectLink(selectedProjectId)}
              projectLinkSaving={projectLinkSaving}
              projectOptions={projectOptions}
              selectedProjectId={selectedProjectId}
              onSelectProject={setSelectedProjectId}
              projectOptionsLoading={projectOptionsLoading}
            />
            <WorkorderMailCard
              canSend={canSendWorkorderMail}
              to={mailTo}
              onToChange={setMailTo}
              subject={mailSubject}
              onSubjectChange={setMailSubject}
              body={mailBody}
              onBodyChange={setMailBody}
              onSend={sendWorkorderMail}
              sending={mailSending}
              error={mailError}
              notice={mailNotice}
            />
            <WerkOpdrachtCard
              werkbon={werkbon}
              canEdit={canEditWerkopdracht}
              value={werkopdrachtText}
              onChange={setWerkopdrachtText}
              onSave={saveWerkopdracht}
              saving={werkopdrachtSaving}
              error={werkopdrachtError}
            />
            {String(werkbon?.sourceOpnameId || "").trim() ? (
              <section className="rounded-xl border bg-white p-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Bronopname / Vooronderzoek
                </p>
                <h3 className="mt-1 text-lg font-semibold text-slate-950">
                  {String(werkbon?.sourceOpnameTitel || "").trim() || "Bronopname"}
                </h3>
                <p className="mt-2 text-sm text-slate-600">
                  Type: {String(werkbon?.sourceOpnameType || "").trim() || "Onbekend"}
                </p>
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/opname?opnameId=${encodeURIComponent(String(werkbon?.sourceOpnameId || ""))}`
                      )
                    }
                    className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Open opname
                  </button>
                </div>
              </section>
            ) : null}
            {bronOpnameDocumentLinks.length > 0 ? (
              <section className="rounded-xl border bg-white p-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Bronopnames / Vooronderzoek
                </p>
                <div className="mt-3 space-y-3">
                  {bronOpnameDocumentLinks.map((item, index) => {
                    const title = String(item?.title || item?.label || "Opname PDF").trim();
                    const label = String(item?.label || "").trim();
                    const sourceId = String(item?.sourceId || "").trim();
                    const typeLabel =
                      sourceId && sourceId === String(werkbon?.sourceOpnameId || "").trim()
                        ? String(werkbon?.sourceOpnameType || "").trim()
                        : "";
                    const viewUrl = getBronDocumentViewUrl(item);
                    const downloadUrl = getBronDocumentDownloadUrl(item);
                    return (
                      <div
                        key={`${String(item?.fileAssetId || item?.sourceId || "bron-document")}-${index}`}
                        className="rounded-lg border border-slate-200 p-3"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{title}</p>
                            <p className="mt-1 text-xs text-slate-500">
                              {label || "Brondocument"}
                              {item?.pdfVersion ? ` • PDF v${item.pdfVersion}` : ""}
                              {typeLabel ? ` • ${typeLabel}` : ""}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              Gepubliceerd: {formatDatumTijd(item?.linkedAt)}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {sourceId ? (
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(`/opname?opnameId=${encodeURIComponent(sourceId)}`)
                                }
                                className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                              >
                                Open opname
                              </button>
                            ) : null}
                            {viewUrl ? (
                              <button
                                type="button"
                                onClick={() => openDocumentInNewTab(viewUrl)}
                                className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                              >
                                Open PDF
                              </button>
                            ) : null}
                            {downloadUrl ? (
                              <button
                                type="button"
                                onClick={() =>
                                  downloadDocumentFile(
                                    downloadUrl,
                                    buildBronDocumentFilename(item, werkbon)
                                  )
                                }
                                className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                              >
                                Download
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : null}
          </div>

          <div className="print-shell space-y-3">
            <SectionTitle title="Beheer & dossiercontrole" subtitle="Corrigeren, aanvullen en afrondkwaliteit bewaken" />

            <div className="rounded-xl border bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="font-semibold">Dossierstatus</h3>
                <span
                  className={`rounded-full px-2 py-1 text-xs font-medium ${
                    completionSignal.tone === "ok"
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-amber-100 text-amber-800"
                  }`}
                >
                  {completionSignal.label}
                </span>
              </div>
              <div className="grid gap-2 text-sm md:grid-cols-2">
                <StatusItem label="Daglog" done={hasDaglog} />
                <StatusItem label="Foto's" done={hasPhotos} />
                <StatusItem label="Materiaal" done={hasMaterials} />
                <StatusItem
                  label="Handtekening"
                  done={
                    werkbon?.completionFlow?.signatureRequired !== true ||
                    werkbon?.completionFlow?.signatureCaptured === true
                  }
                />
              </div>
              {completionMissingItems.length > 0 && (
                <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <p className="font-semibold">Nog te controleren/aan te vullen</p>
                  <ul className="mt-1 list-disc space-y-0.5 pl-4">
                    {completionMissingItems.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

          <details className="bg-white border rounded-xl p-4 shadow-sm">
            <summary className="cursor-pointer font-semibold">Planning (secundair, primair via planner)</summary>
            <div className="mt-3">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h3 className="font-semibold">Planning</h3>
            </div>

            {!canEditPlanning ? (
              <div className="text-sm space-y-1">
                <p>
                  <strong>Geplande datum:</strong>{" "}
                  {planningForm.plannedDate ? formatDatum(planningForm.plannedDate) : "-"}
                </p>
                <p>
                  <strong>Geplande starttijd:</strong>{" "}
                  {planningForm.plannedStartTime || "-"}
                </p>
                <p>
                  <strong>Geplande tijd:</strong>{" "}
                  {formatPlannedMinutes(planningForm.plannedMinutes)}
                </p>
                <p>
                  <strong>Toegewezen aan:</strong>{" "}
                  {resolveAssignedUserName(werkbon, planningUsers)}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="rounded border border-slate-200 bg-slate-50 p-3">
                  <p className="text-sm font-semibold text-slate-900">Planactie</p>
                  <p className="mt-1 text-xs text-slate-600">
                    Zet deze werkbon eerst in de werkvoorraad of plan hem direct in met datum en monteur.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={sendToWorkvoorraad}
                      disabled={workflowSaving || planningSaving}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 disabled:opacity-60"
                    >
                      {workflowSaving ? "Bezig..." : "Zet in werkvoorraad"}
                    </button>
                    <button
                      type="button"
                      onClick={directPlanWerkbon}
                      disabled={workflowSaving || planningSaving}
                      className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
                    >
                      {planningSaving ? "Bezig..." : "Direct inplannen"}
                    </button>
                  </div>
                </div>

                <div
                  className={`rounded border px-3 py-3 text-sm ${
                    planningRequirements.readyForPlanned
                      ? "border-green-200 bg-green-50 text-green-800"
                      : "border-red-200 bg-red-50 text-red-700"
                  }`}
                >
                  <p className="font-semibold">
                    {planningRequirements.readyForPlanned
                      ? "Klaar voor status 'gepland'"
                      : "Nog nodig voor status 'gepland'"}
                  </p>
                  {!planningRequirements.readyForPlanned && (
                    <p className="mt-1">
                      Verplicht: {planningRequirements.missingLabels.join(", ")}.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label
                      className={`block text-sm mb-1 ${
                        planningRequirements.missing.plannedDate ? "text-red-700 font-medium" : ""
                      }`}
                    >
                      Geplande datum
                      {planningRequirements.missing.plannedDate ? " * verplicht" : ""}
                    </label>
                    <input
                      type="date"
                      value={planningForm.plannedDate}
                      onChange={(e) =>
                        {
                          setPlanningForm((prev) => ({
                            ...prev,
                            plannedDate: e.target.value,
                          }));
                          setPlanningFieldErrors((prev) => ({ ...prev, plannedDate: undefined }));
                        }
                      }
                      className={`w-full rounded border p-2 ${
                        planningRequirements.missing.plannedDate
                          ? "border-red-300 bg-red-50 text-red-900"
                          : ""
                      }`}
                    />
                    {planningFieldErrors?.plannedDate?.[0] && (
                      <p className="mt-1 text-xs text-red-600">{planningFieldErrors.plannedDate[0]}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Geplande starttijd</label>
                    <input
                      type="time"
                      value={planningForm.plannedStartTime}
                      onChange={(e) =>
                        {
                          setPlanningForm((prev) => ({
                            ...prev,
                            plannedStartTime: e.target.value,
                          }));
                          setPlanningFieldErrors((prev) => ({ ...prev, plannedStartTime: undefined }));
                        }
                      }
                      className="w-full rounded border p-2"
                    />
                    {planningFieldErrors?.plannedStartTime?.[0] && (
                      <p className="mt-1 text-xs text-red-600">{planningFieldErrors.plannedStartTime[0]}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label
                    className={`block text-sm mb-1 ${
                      planningRequirements.missing.assignedTo ? "text-red-700 font-medium" : ""
                    }`}
                  >
                    Toegewezen aan
                    {planningRequirements.missing.assignedTo ? " * verplicht" : ""}
                  </label>
                  <select
                    value={planningForm.assignedTo}
                    onChange={(e) =>
                      {
                        setPlanningForm((prev) => ({
                          ...prev,
                          assignedTo: e.target.value,
                        }));
                        setPlanningFieldErrors((prev) => ({ ...prev, assignedTo: undefined }));
                      }
                    }
                    className={`w-full rounded border p-2 ${
                      planningRequirements.missing.assignedTo
                        ? "border-red-300 bg-red-50 text-red-900"
                        : ""
                    }`}
                  >
                    <option value="">-- kies monteur --</option>
                    {planningUsers.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.naam || user.email || user._id}
                      </option>
                    ))}
                  </select>
                  {planningFieldErrors?.assignedTo?.[0] && (
                    <p className="mt-1 text-xs text-red-600">{planningFieldErrors.assignedTo[0]}</p>
                  )}
                </div>

                {(planningFieldErrors?.plannedMinutes?.[0] || planningFieldErrors?.plannedDurationMinutes?.[0]) && (
                  <p className="text-xs text-red-600">
                    {planningFieldErrors?.plannedMinutes?.[0] ||
                      planningFieldErrors?.plannedDurationMinutes?.[0]}
                  </p>
                )}

                <div className="rounded border bg-gray-50 p-3 text-sm">
                  {planningRequirements.missing.location && (
                    <div className="mb-2 inline-flex rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-700">
                      Verplicht veld ontbreekt
                    </div>
                  )}
                  <p
                    className={
                      planningRequirements.missing.location ? "font-medium text-red-700" : "font-medium"
                    }
                  >
                    Locatie
                    {planningRequirements.missing.location ? " * verplicht" : ""}
                  </p>
                  <p className="mt-1 text-gray-700">
                    {getWerkbonLocationLabel(werkbon)}
                  </p>
                  {planningRequirements.missing.location && (
                    <p className="mt-2 text-red-700">
                      Voeg eerst een locatie toe op de werkbon of opdracht. Anders blijft deze op
                      gereed voor planning staan.
                    </p>
                  )}
                </div>

                {planningError && <p className="text-red-600 text-sm">{planningError}</p>}

                <button
                  type="button"
                  onClick={savePlanning}
                  disabled={planningSaving}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm disabled:opacity-60"
                >
                  {planningSaving ? "Opslaan..." : "Planninggegevens opslaan"}
                </button>
              </div>
            )}
            </div>
          </details>

            <details className="rounded-xl border bg-white p-4 shadow-sm">
              <summary className="cursor-pointer font-semibold">Urencontrole (secundair)</summary>
              <div className="mt-3">
                <UrenCard
                  werkbon={werkbon}
                  financial={financial}
                  onRefresh={loadWerkbon}
                />
              </div>
            </details>

            <details className="bg-white border rounded-xl p-4 shadow-sm">
              <summary className="cursor-pointer font-semibold">Acties monteur (secundair)</summary>
              <div className="mt-3">
              <h3 className="font-semibold mb-3">Acties monteur</h3>
              {getWerkbonActies(werkbon).length === 0 ? (
                <p className="text-sm text-gray-500">Nog geen acties vastgelegd.</p>
              ) : (
                <ul className="space-y-2">
                  {getWerkbonActies(werkbon).map((actie, idx) => (
                    <li key={`${actie.id || "actie"}-${idx}`} className="rounded border p-3 text-sm">
                      <p className="text-xs uppercase tracking-wide text-gray-500">
                        {actie.type} {actie.status ? `• ${actie.status}` : ""}
                      </p>
                      <p className="font-medium">{actie.title}</p>
                    </li>
                  ))}
                </ul>
              )}
              </div>
            </details>

            <ProjectLeaderDaylogTabs
              workorderId={id}
              financial={financial}
              legacyNotes={Array.isArray(werkbon?.daglog) ? werkbon.daglog : []}
            />



            <FotosCard
              werkbon={werkbon}
              onAddFoto={() => setFotoModalOpen(true)}
            />

            <MaterialenCard
              werkbon={werkbon}
              financial={financial}
              onRefresh={loadWerkbon}
            />

            <AfrondenCard
              werkbon={werkbon}
              onAfronden={afrondenWerkbon}
              statusNotice={afrondStatusNotice}
            />

            <details className="rounded-xl border bg-white p-4 shadow-sm">
              <summary className="cursor-pointer font-semibold">Documenten</summary>
              <div className="mt-4">
                <div className="flex items-center gap-3">
                  <label className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm cursor-pointer">
                    {bijlageUploading ? "Uploaden..." : "Bijlage uploaden"}
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleBijlageUpload}
                      disabled={bijlageUploading}
                    />
                  </label>
                  <span className="text-xs text-gray-500">
                    Ondersteunt o.a. PDF, DOCX, XLSX, JPG, PNG.
                  </span>
                </div>
                {bijlageError && <p className="text-red-600 text-sm mt-2">{bijlageError}</p>}

                <div className="mt-3 space-y-2">
                  {!Array.isArray(werkbon?.bijlagen) || werkbon.bijlagen.length === 0 ? (
                    <p className="text-sm text-gray-500">Nog geen bijlagen toegevoegd.</p>
                  ) : (
                    werkbon.bijlagen.map((item, idx) => (
                      <div
                        key={`${item.bestand || "bijlage"}-${idx}`}
                        className="flex items-center justify-between border rounded p-2 text-sm"
                      >
                        <div>
                          <p className="font-medium">{item.naam || item.bestand || "Bijlage"}</p>
                          <p className="text-xs text-gray-500">
                            {item.type || "onbekend"} • {toFileSize(item.grootte)} •{" "}
                            {formatDatumTijd(item.uploadedOp)}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <a
                            href={toUploadUrl(item.bestand)}
                            target={shouldPreviewAttachment(item) ? "_blank" : undefined}
                            rel={shouldPreviewAttachment(item) ? "noreferrer" : undefined}
                            download={
                              shouldPreviewAttachment(item)
                                ? undefined
                                : item.naam || item.bestand || "bijlage"
                            }
                            className="text-blue-600 hover:underline"
                          >
                            {shouldPreviewAttachment(item) ? "Open" : "Download"}
                          </a>
                          <button
                            type="button"
                            onClick={() => handleBijlageDelete(item)}
                            disabled={String(bijlageDeletingId || "") === String(item?._id || "")}
                            className="text-red-600 hover:underline disabled:opacity-60"
                          >
                            {String(bijlageDeletingId || "") === String(item?._id || "") ? "Verwijderen..." : "Verwijder"}
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </details>

            <details className="rounded-xl border bg-white p-4 shadow-sm">
              <summary className="cursor-pointer font-semibold">Financieel</summary>
              <div className="mt-4">
                {financialError && (
                  <p className="text-red-600 text-sm mb-2">{financialError}</p>
                )}
                <div className="text-sm space-y-1">
                  <p>Uren excl: € {toMoney(financial?.financialTotals?.hoursSubtotal)}</p>
                  <p>Materiaal excl: € {toMoney(financial?.financialTotals?.materialsSubtotal)}</p>
                  <p>Subtotaal excl: € {toMoney(financial?.financialTotals?.subtotalExcl)}</p>
                  <p>BTW: € {toMoney(financial?.financialTotals?.vatTotal)}</p>
                  <p className="font-semibold">
                    Totaal incl: € {toMoney(financial?.financialTotals?.totalIncl)}
                  </p>
                  <p>
                    Tijdmodus:{" "}
                    {werkbon?.timeMode === "auto"
                      ? "Automatisch (systeem)"
                      : "Handmatig"}
                  </p>
                  <p>Tijdgoedkeuring: {werkbon?.timeApprovalStatus || "nvt"}</p>
                  <p>Ingediend: {werkbon?.timeSubmissionStatus || "concept"}</p>
                  {werkbon?.timeApprovalStatus === "afgekeurd" &&
                    werkbon?.timeApprovalRejectReason && (
                      <p className="text-red-700">
                        Afkeurreden: {werkbon.timeApprovalRejectReason}
                      </p>
                    )}
                  <p>
                    Moneybird factuur-id:{" "}
                    {financial?.moneybirdInvoiceId || werkbon?.moneybirdInvoiceId || "-"}
                  </p>
                </div>

                {werkbon?.timeMode === "auto" &&
                  werkbon?.timeSubmissionStatus === "ingediend" &&
                  werkbon?.timeApprovalStatus === "te_controleren" && (
                    <div className="mt-3 rounded border p-3 space-y-2">
                      <p className="text-sm font-semibold">Uren goedkeuring</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => decideTimeApproval("goedgekeurd")}
                          disabled={timeDecisionLoading}
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-sm disabled:opacity-60"
                        >
                          Goedkeuren
                        </button>
                        <button
                          onClick={() => decideTimeApproval("afgekeurd")}
                          disabled={timeDecisionLoading}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm disabled:opacity-60"
                        >
                          Afkeuren
                        </button>
                      </div>
                      <textarea
                        rows={2}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Afkeurreden (verplicht bij afkeuren)"
                        className="w-full rounded border p-2 text-sm"
                      />
                    </div>
                  )}

                <button
                  onClick={maakFactuurSnapshot}
                  disabled={factuurMakenLoading}
                  className="mt-3 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm disabled:opacity-60"
                >
                  {factuurMakenLoading ? "Bezig..." : "Maak factuur-snapshot"}
                </button>
              </div>
            </details>
          </div>
        </div>
      </div>


      {/* ================= FOTO MODAL ================= */}
      {fotoModalOpen && (

        <FotoUploadMetPintura
          werkbonId={id}
          onClose={() => setFotoModalOpen(false)}
          onSaved={() => {
            setFotoModalOpen(false);
            loadWerkbon();
          }}
        />

      )}
    </>
  );
}

function toMoney(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "0.00";
  return num.toFixed(2);
}

function SectionTitle({ title, subtitle }) {
  return (
    <div className="no-print rounded-xl border bg-white p-4 shadow-sm">
      <h2 className="text-lg font-semibold">{title}</h2>
      {subtitle && <p className="mt-1 text-sm text-gray-600">{subtitle}</p>}
    </div>
  );
}

function SummaryTile({ label, value }) {
  return (
    <div className="rounded-xl border bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-2 text-sm text-gray-800">{value || "-"}</p>
    </div>
  );
}

function WorkorderMailCard({
  canSend = false,
  to = "",
  onToChange,
  subject = "",
  onSubjectChange,
  body = "",
  onBodyChange,
  onSend,
  sending = false,
  error = "",
  notice = "",
}) {
  return (
    <div className="rounded-xl border bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="font-semibold">Klant mailen</h3>
        {!canSend ? (
          <span className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-600">Alleen lezen</span>
        ) : null}
      </div>

      <div className="space-y-3 text-sm">
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Ontvanger</span>
          <input
            type="email"
            value={to}
            onChange={(event) => onToChange?.(event.target.value)}
            disabled={!canSend || sending}
            placeholder="klant@bedrijf.nl"
            className="w-full rounded border border-slate-300 px-3 py-2"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Onderwerp</span>
          <input
            type="text"
            value={subject}
            onChange={(event) => onSubjectChange?.(event.target.value)}
            disabled={!canSend || sending}
            placeholder="Onderwerp"
            className="w-full rounded border border-slate-300 px-3 py-2"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Bericht</span>
          <textarea
            rows={5}
            value={body}
            onChange={(event) => onBodyChange?.(event.target.value)}
            disabled={!canSend || sending}
            placeholder="Typ je bericht..."
            className="w-full rounded border border-slate-300 px-3 py-2"
          />
        </label>

        {error ? (
          <p className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</p>
        ) : null}
        {notice ? (
          <p className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">{notice}</p>
        ) : null}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onSend}
            disabled={!canSend || sending}
            className="rounded bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {sending ? "Verzenden..." : "Mail verzenden"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusItem({ label, done }) {
  return (
    <p className={`rounded border px-3 py-2 ${done ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
      <span className="font-medium">{label}:</span> {done ? "ok" : "aandacht nodig"}
    </p>
  );
}

function PrintField({ label, value }) {
  return (
    <div>
      <p className="font-semibold">{label}</p>
      <p className="mt-1">{value || "-"}</p>
    </div>
  );
}

function PrintBox({ title }) {
  return (
    <div>
      <h3 className="mb-2 font-semibold">{title}</h3>
      <div className="min-h-[120px] rounded border" />
    </div>
  );
}

function getPrintContactLabel(werkbon) {
  const value = [
    werkbon?.contact?.name,
    werkbon?.contact?.role,
    werkbon?.contact?.phone,
    werkbon?.contact?.email,
  ]
    .filter(Boolean)
    .join(" • ");

  return value || (werkbon?.contact?.unknown ? "Onbekend" : "-");
}

function getWerkopdrachtLabel(werkbon) {
  return (
    werkbon?.omschrijving ||
    werkbon?.opdracht?.omschrijving ||
    werkbon?.scope?.samenvatting ||
    "Geen werkopdracht opgegeven."
  );
}

function getWerkbonActies(werkbon) {
  const acties = Array.isArray(werkbon?.acties) ? werkbon.acties : [];
  if (acties.length > 0) {
    return acties.map((actie) => ({
      id: actie?._id || "",
      title: actie?.title || "-",
      type: formatActionTypeLabel(actie?.type),
      status: formatActionStatusLabel(actie?.status),
    }));
  }

  const legacy = Array.isArray(werkbon?.uitTeVoerenWerkzaamheden)
    ? werkbon.uitTeVoerenWerkzaamheden
    : [];

  return legacy.map((line) => {
    const parsed = parseLegacyActionLine(line);
    return {
      id: "",
      title: parsed.title,
      type: parsed.type,
      status: "Open",
    };
  });
}

function parseLegacyActionLine(value) {
  const raw = String(value || "").trim();
  const match = raw.match(/^\[(.+?)\]\s*(.*)$/);
  if (!match) {
    return { type: "Actie", title: raw || "-" };
  }
  const [, type, title] = match;
  return {
    type: String(type || "").trim() || "Actie",
    title: String(title || "").trim() || raw || "-",
  };
}

function formatActionTypeLabel(value) {
  const key = String(value || "").trim().toLowerCase();
  if (key === "voorbereiding") return "Voorbereiding";
  if (key === "uitvoering") return "Uitvoering";
  if (key === "blokkade") return "Blokkade: ontbrekende info";
  if (key === "nazorg") return "Nazorg";
  if (key === "overig") return "Overig";
  return key ? key : "Actie";
}

function formatActionStatusLabel(value) {
  const key = String(value || "").trim().toLowerCase();
  if (key === "open") return "Open";
  if (key === "bezig") return "Bezig";
  if (key === "wacht-op-info") return "Wacht op info";
  if (key === "gesloten") return "Gesloten";
  return key ? key : "";
}

function getLatestPhotoPreviewItems(werkbon, latestWorkorderPhotos = []) {
  const secureFeed = Array.isArray(latestWorkorderPhotos) ? latestWorkorderPhotos : [];
  const secureMapped = secureFeed
    .map((photo, index) => ({
      id: String(photo?._id || `secure-${index}`),
      url: resolveWerkbonPhotoUrl(photo?.url || photo?.fileUrl || photo?.filename || ""),
      thumbUrl: resolveWerkbonPhotoUrl(
        photo?.thumbnailUrl || photo?.thumbnailFilename || photo?.fileUrl || photo?.url || ""
      ),
      category: String(photo?.category || "Overig"),
      createdAt: photo?.createdAt || null,
    }))
    .filter((photo) => Boolean(photo.url));

  if (secureMapped.length > 0) {
    return secureMapped
      .sort((a, b) => {
        const aTime = new Date(a?.createdAt || 0).getTime();
        const bTime = new Date(b?.createdAt || 0).getTime();
        return bTime - aTime;
      })
      .slice(0, 3);
  }

  const modern = Array.isArray(werkbon?.photos) ? werkbon.photos : [];
  const modernMapped = modern
    .map((photo, index) => ({
      id: String(photo?._id || `modern-${index}`),
      url: resolveWerkbonPhotoUrl(photo?.url || photo?.filename || ""),
      thumbUrl: resolveWerkbonPhotoUrl(photo?.thumbnailUrl || photo?.thumbnailFilename || ""),
      category: String(photo?.category || "Overig"),
      createdAt: photo?.createdAt || null,
    }))
    .filter((photo) => Boolean(photo.url));

  const legacy = Array.isArray(werkbon?.fotos) ? werkbon.fotos : [];
  const legacyMapped = legacy
    .map((value, index) => ({
      id: `legacy-${index}`,
      url: resolveWerkbonPhotoUrl(value),
      thumbUrl: resolveWerkbonPhotoUrl(value),
      category: "Overig",
      createdAt: null,
    }))
    .filter((photo) => Boolean(photo.url));

  return [...modernMapped, ...legacyMapped]
    .sort((a, b) => {
      const aTime = new Date(a?.createdAt || 0).getTime();
      const bTime = new Date(b?.createdAt || 0).getTime();
      return bTime - aTime;
    })
    .slice(0, 3);
}

function resolveWerkbonPhotoUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("/api/files/")) return resolveAssetUrl(raw);

  const apiBase = String(API_BASE_URL).replace(
    /\/api\/?$/,
    ""
  );
  if (!apiBase) return raw;

  if (raw.startsWith("/uploads/")) {
    return `${apiBase}${raw}`;
  }
  if (raw.includes("/")) {
    return resolveAssetUrl(raw);
  }
  return `${apiBase}/uploads/werkbonnen/${encodeURIComponent(raw)}`;
}

function formatDatum(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("nl-NL");
}

function getCompletionSignal(werkbon) {
  const missing = getCompletionMissingItems(werkbon);
  if (missing.length === 0) {
    return { tone: "ok", label: "Compleet genoeg voor afronding" };
  }
  return { tone: "warn", label: `Nog ${missing.length} aandachtspunt(en)` };
}

function getCompletionMissingItems(werkbon) {
  const completionState = werkbon?.completionState || {};
  const missingKeys = Array.isArray(completionState?.missing) ? completionState.missing : [];
  const items = [];

  for (const key of missingKeys) {
    if (key === "executionLog") items.push("Daglog/uitvoeringstekst");
    if (key === "hours") items.push("Tijdregistratie");
    if (key === "materials") items.push("Materiaalregistratie");
    if (key === "evidence") items.push("Foto of bijlage");
  }

  if (
    werkbon?.completionFlow?.signatureRequired === true &&
    werkbon?.completionFlow?.signatureCaptured !== true
  ) {
    items.push("Klant-handtekening");
  }

  return Array.from(new Set(items));
}

function toDateInputValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getAssignedToValue(werkbon) {
  if (werkbon?.assignedTo && typeof werkbon.assignedTo === "object") {
    return String(werkbon.assignedTo._id || "");
  }
  return String(werkbon?.assignedTo || "");
}

function getProjectIdValue(werkbon) {
  if (!werkbon?.projectId) return "";
  if (typeof werkbon.projectId === "string") return String(werkbon.projectId);
  return String(werkbon.projectId?._id || "");
}

function formatProjectLabel(project) {
  const nummer = String(project?.projectNummer || project?.number || "").trim();
  const naam = String(project?.naam || project?.title || "").trim();
  if (nummer && naam) return `${nummer} - ${naam}`;
  return nummer || naam || "Onbekend project";
}

function getWerkbonLocationLabel(werkbon) {
  return (
    String(
      werkbon?.locationText ||
        werkbon?.opdracht?.locatie ||
        werkbon?.adres ||
        ""
    ).trim() || "Locatie ontbreekt"
  );
}

function getPlanningRequirements(werkbon, form = {}) {
  const missing = {
    assignedTo: !String(form?.assignedTo || getAssignedToValue(werkbon) || "").trim(),
    plannedDate: !String(form?.plannedDate || toDateInputValue(werkbon?.plannedDate) || "").trim(),
    location: !String(
      werkbon?.locationText ||
        werkbon?.opdracht?.locatie ||
        werkbon?.adres ||
        ""
    ).trim(),
  };

  const missingLabels = [];
  if (missing.assignedTo) missingLabels.push("monteur");
  if (missing.plannedDate) missingLabels.push("datum");
  if (missing.location) missingLabels.push("locatie");

  return {
    missing,
    missingLabels,
    readyForPlanned: missingLabels.length === 0,
  };
}

function asUsersArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.users)) return data.users;
  return [];
}

function resolveAssignedUserName(werkbon, users = []) {
  if (werkbon?.assignedTo && typeof werkbon.assignedTo === "object") {
    return werkbon.assignedTo.naam || werkbon.assignedTo.email || "-";
  }
  const assignedTo = String(werkbon?.assignedTo || "");
  if (!assignedTo) return "-";
  const found = users.find((user) => String(user._id) === assignedTo);
  return found?.naam || found?.email || assignedTo;
}

function formatDatumTijd(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("nl-NL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function toFileSize(value) {
  const size = Number(value || 0);
  if (!Number.isFinite(size) || size <= 0) return "0 B";
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  if (size >= 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${size} B`;
}

function getRoleNames(user) {
  const roleSet = new Set();
  if (user?.rol) roleSet.add(String(user.rol).trim().toLowerCase());
  if (user?.role) roleSet.add(String(user.role).trim().toLowerCase());
  for (const role of user?.roles || []) {
    if (typeof role === "string") {
      roleSet.add(String(role).trim().toLowerCase());
      continue;
    }
    const mapped = role?.naam || role?.name;
    if (mapped) roleSet.add(String(mapped).trim().toLowerCase());
  }
  return Array.from(roleSet);
}

function firstFieldError(fieldErrors, keys = []) {
  for (const key of keys) {
    const value = fieldErrors?.[key];
    if (Array.isArray(value) && value[0]) return String(value[0]);
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

function toUploadUrl(bestand) {
  const apiBase = API_BASE_URL;
  const safe = String(bestand || "").replace(/^\/+/, "");
  return `${apiBase}/uploads/werkbonnen/${safe}`;
}

function getApiOrigin() {
  const baseUrl = String(api?.defaults?.baseURL || "").trim();
  if (!baseUrl) return "";
  return baseUrl.replace(/\/api\/?$/, "");
}

function resolveAssetUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("blob:") || raw.startsWith("data:")) return raw;

  const apiOrigin = getApiOrigin();
  if (!apiOrigin) {
    if (raw.startsWith("/")) return raw;
    if (raw.includes("/")) return `/${raw.replace(/^\/+/, "")}`;
    return raw;
  }

  if (raw.startsWith("/")) return `${apiOrigin}${raw}`;
  if (raw.includes("/")) return `${apiOrigin}/${raw.replace(/^\/+/, "")}`;
  return raw;
}

function isSecureFileUrl(value) {
  const raw = String(value || "").trim();
  return (
    /^\/api\/files\/[^/]+\/(view|download)$/i.test(raw) ||
    /^https?:\/\/[^/]+\/api\/files\/[^/]+\/(view|download)$/i.test(raw)
  );
}

function getBronDocumentViewUrl(item) {
  const explicit = String(item?.viewUrl || item?.fileUrl || item?.url || "").trim();
  if (explicit) return resolveAssetUrl(explicit);
  const fileAssetId = String(item?.fileAssetId || item?.fileId || "").trim();
  if (!fileAssetId) return "";
  return resolveAssetUrl(`/api/files/${encodeURIComponent(fileAssetId)}/view`);
}

function getBronDocumentDownloadUrl(item) {
  const explicit = String(item?.downloadUrl || "").trim();
  if (explicit) return resolveAssetUrl(explicit);
  const fileAssetId = String(item?.fileAssetId || item?.fileId || "").trim();
  if (!fileAssetId) return "";
  return resolveAssetUrl(`/api/files/${encodeURIComponent(fileAssetId)}/download`);
}

async function fetchDocumentBlob(url) {
  const value = String(url || "").trim();
  if (!value) throw new Error("Document URL ontbreekt");
  if (isSecureFileUrl(value)) {
    const requestUrl = resolveAssetUrl(value);
    const res = await api.get(requestUrl, { responseType: "blob" });
    return res.data;
  }
  const res = await fetch(resolveAssetUrl(value));
  if (!res.ok) throw new Error("Document ophalen mislukt");
  return res.blob();
}

async function openDocumentInNewTab(url) {
  try {
    const blob = await fetchDocumentBlob(url);
    const objectUrl = window.URL.createObjectURL(blob);
    window.open(objectUrl, "_blank", "noopener,noreferrer");
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
  } catch (error) {
    console.error("Document openen mislukt", error);
    window.alert("Document openen mislukt.");
  }
}

async function downloadDocumentFile(url, filename) {
  try {
    const blob = await fetchDocumentBlob(url);
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = String(filename || "document.pdf").trim() || "document.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
  } catch (error) {
    console.error("Document downloaden mislukt", error);
    window.alert("Document downloaden mislukt.");
  }
}

function buildBronDocumentFilename(item, werkbon) {
  const number = String(werkbon?.werkbonNummer || werkbon?.nummer || "werkbon").trim();
  const title = String(item?.title || item?.label || "opname-pdf")
    .trim()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  const version = item?.pdfVersion ? `-v${item.pdfVersion}` : "";
  return `${number}-${title || "opname-pdf"}${version}.pdf`;
}

function shouldPreviewAttachment(item) {
  const mimeType = String(item?.type || item?.mimeType || "").trim().toLowerCase();
  if (mimeType === "application/pdf" || mimeType.startsWith("image/")) {
    return true;
  }

  const previewExtensions = [
    "pdf",
    "png",
    "jpg",
    "jpeg",
    "gif",
    "webp",
    "bmp",
    "svg",
    "tif",
    "tiff",
  ];

  const candidates = [item?.bestand, item?.naam];
  return candidates.some((candidate) => {
    const normalized = String(candidate || "")
      .trim()
      .toLowerCase()
      .split("#")[0]
      .split("?")[0];
    if (!normalized) return false;
    const ext = normalized.includes(".") ? normalized.split(".").pop() : "";
    return previewExtensions.includes(String(ext || ""));
  });
}
